import { useEffect, useRef } from 'react';
import { subscribe } from 'lib/pusher';

/**
 * Pusher's own "your subscription is now registered" event.
 *
 * This is the event the whole module exists for. A client subscribes AFTER the POST that
 * starts the job returns, and Pusher does NOT replay: anything published between the server
 * accepting the job and the subscription being registered is lost permanently, and the page
 * waits forever for an event that already happened. On a fast run both publishes land within
 * a few seconds of the click, so the window is not an edge case — it is the common case for
 * anything that finishes quickly.
 *
 * `pusher:subscription_succeeded` is the exact instant the two halves can be made to meet:
 * once it has fired, every LATER publish on the channel will arrive, and a read taken now
 * captures everything published BEFORE it. No gap. It also re-fires after a reconnect, so a
 * socket dropped mid-job heals on the same code path.
 *
 * In-repo precedent for the bind: `components/well-page-content/subsurface-explorer/helpers.ts`.
 */
export const PUSHER_SUBSCRIPTION_SUCCEEDED = 'pusher:subscription_succeeded';

/**
 * The part of a pusher-js `Channel` this module touches. Deliberately NOT `unsubscribe`:
 * `armJobCompletion` does not own the channel's lifetime. pusher-js hands back the SAME
 * Channel object for a name already in its registry, and callers bind their own handlers to
 * it, so who unsubscribes is the caller's decision — this module only ever removes what it
 * itself bound.
 *
 * `unbind` is REQUIRED, not optional. Made optional it would be legal to pass a channel that
 * cannot be unbound, and "unbinds exactly what it bound" would degrade to a silent no-op —
 * leaking a live handler onto an object pusher-js hands back by name. Every channel in this
 * codebase has it (pusher-js's `Channel`, the `fakeChannel` in `lib/pusher`, `MockPusher`),
 * so requiring it costs nothing and turns a silent leak into a type error.
 */
export interface JobChannel {
  bind: (event: string, handler: (data?: unknown) => void) => void;
  unbind: (event: string, handler: (data?: unknown) => void) => void;
}

/**
 * The poll backstop: the trigger for Pusher being unreachable ENTIRELY (a blocked WebSocket,
 * a dead socket that never reconnects). It is not the primary path and is not meant to be —
 * `delayMs` exists so Pusher always gets first refusal, and jobs that settle inside it cost
 * nothing.
 */
export interface JobPollConfig {
  /** How long to leave Pusher alone before the first reconcile. */
  delayMs: number;
  /** Gap between reconciles once they start. */
  intervalMs: number;
  /**
   * When to stop reconciling a server that is not answering, measured from when the poll
   * ARMED. This is not a verdict on the job — see `onExpired`.
   */
  maxMs: number;
  /**
   * Epoch ms the INITIAL DELAY is measured from. Defaults to the arming time, i.e. the full
   * `delayMs` is served from now.
   *
   * This is a real choice, not a detail, and the two Eval 3.0 pages land on opposite sides of
   * it — so pick deliberately. Passing the JOB RECORD's clock means a job that has already
   * been waiting does not serve the wait twice, and a job resumed long after it started is
   * read at once; passing nothing (or the client's own arming clock) means every arming
   * serves the full grace period, and a resumed job is left alone for `delayMs` however long
   * it has already been running. Record-clock is right when the read is cheap and a stuck job
   * should be caught immediately; arming-clock is right when the read is expensive, or when
   * the record's clock does not actually track THIS attempt — a re-run that reuses its record
   * keeps the original `created_at`, so a record clock would compute a delay of zero for the
   * single most common kind of run.
   *
   * Read ONCE when the poll arms; changing it later does not re-arm anything. A value that is
   * absent, non-finite or non-positive is treated as "not supplied" and the full delay is
   * served — the one thing it must never do is silently switch the grace period off. The
   * result is also clamped to `delayMs`, so a `since` in the FUTURE (a server-generated
   * timestamp plus clock skew) cannot delay the first read beyond the normal grace period.
   * Whether that keeps it inside `maxMs` depends on the caller passing `delayMs < maxMs`,
   * which is not enforced here — though a first read is taken either way, because a tick that
   * finds the cap already due still reads before expiring.
   */
  since?: number;
  /**
   * Called when `maxMs` elapses, AFTER the record has been given the last word. For a caller
   * whose only way to end a wait is a client-side timeout. Callers whose server owns terminal
   * status (the better design) should omit it: the record decides, and a client that decides
   * nothing cannot report a failure that did not happen.
   *
   * Called AT MOST ONCE per arming, and that is a guarantee rather than an accident of timing:
   * past the cap the in-flight guard is deliberately dropped, so ticks overlap, and without a
   * latch two of them can reach here. Any caller that surfaces a message, resets state or
   * unsubscribes from this would then do it twice.
   *
   * Captured when the poll ARMS. {@link useJobCompletion} re-reads it per call, so a caller
   * using the hook may pass an inline closure; a caller using `armJobCompletion` directly
   * gets the function it passed.
   */
  onExpired?: () => void;
}

/** What to reconcile, and what should trigger it. */
export interface JobCompletionConfig {
  /** An already-subscribed channel, or null to bind nothing (the poll can still arm). */
  channel: JobChannel | null;
  /**
   * Event names whose ARRIVAL should trigger a reconcile. Omit if the caller binds its own
   * handler for the terminal event because it consumes the payload — the event is then that
   * caller's business, not this module's, and explicitly outside the guarantee described on
   * `reconcile`. Duplicates, and {@link PUSHER_SUBSCRIPTION_SUCCEEDED} itself, are bound once.
   */
  events?: readonly string[];
  /**
   * The authoritative read, and the only thing any trigger ROUTED THROUGH THIS MODULE does.
   *
   * This is the rule the design rests on: the event is a hint, never the data. Every trigger
   * this module owns — subscription_succeeded, a named event, a poll tick — converges here,
   * and none of them carries any state. `reconcile` takes no arguments by construction, so
   * nothing a payload claims can reach the screen through it: for state that arrives this way,
   * a false "failed" or a false "still running" is ruled out by the shape of the code rather
   * than guarded against at runtime.
   *
   * THE SCOPE OF THAT CLAIM, precisely, because it is easy to over-read: it covers what comes
   * through `reconcile`, NOT the page. A caller may legitimately bind its own handler for an
   * event it consumes the payload of (see `events`), and whatever that handler decides is
   * outside this guarantee entirely. The legacy Eval 3 page — the page this module was
   * extracted from — does exactly that, and takes a failure verdict straight off the payload:
   * `resolveError(data.errorMessage ?? 'Calculation failed')`. Adopting this module does not
   * by itself make a page payload-free; routing a given state through `reconcile` is what
   * makes THAT state safe.
   *
   * Must be idempotent; it will run more than once per job.
   */
  reconcile: () => void | Promise<void>;
  /** The poll backstop, or null/undefined for none. */
  poll?: JobPollConfig | null;
}

/**
 * Arms the reconcile triggers on an already-subscribed channel and returns a disarm function.
 *
 * The imperative core. Use {@link useJobCompletion} from a component; use this directly only
 * where the subscription cannot be driven from an effect (a callback fired by a POST
 * continuation, say), where the caller binds its own handlers to the same channel, or where
 * the channel NAME is shared with another component (see {@link useJobCompletion} on why the
 * hook must not be used there).
 *
 * @param {JobCompletionConfig} config - Channel, triggers and the reconcile.
 * @return {Function} Disarms: stops the poll and unbinds exactly what this call bound.
 *   Does NOT unsubscribe — see {@link JobChannel}. Safe to call more than once.
 */
export function armJobCompletion({
  channel,
  events,
  reconcile,
  poll,
}: JobCompletionConfig): () => void {
  const bound: Array<{ event: string; handler: (data?: unknown) => void }> = [];
  let disarmed = false;
  // How many reconciles are outstanding. A COUNT, not a flag: reconciles can overlap (an
  // event never waits for a tick — see below), and a flag would be cleared by whichever
  // finished first while others were still running.
  let inFlightCount = 0;

  /**
   * Runs the reconcile, swallowing a rejection.
   *
   * A read that failed means "no answer yet", not "the job failed" — the next trigger will
   * ask again. Letting it reject would surface an unhandled rejection from a timer or an
   * event handler, and there is nothing here that could act on it: this module never decides
   * anything about the job.
   *
   * @return {Promise<void>} Resolves when the reconcile has settled, either way.
   */
  const runReconcile = async () => {
    inFlightCount += 1;
    try {
      await reconcile();
    } catch {
      // Intentionally ignored — see above.
    } finally {
      inFlightCount -= 1;
    }
  };

  if (channel) {
    const alreadyBound = new Set<string>();
    const bindReconcile = (event: string) => {
      // Binding the same name twice would reconcile twice per arrival — two reads, and with a
      // react-query reconcile the second aborts the first. A caller listing
      // `pusher:subscription_succeeded` among its own events is the likely way in.
      if (alreadyBound.has(event)) return;
      alreadyBound.add(event);
      const handler = () => void runReconcile();
      channel.bind(event, handler);
      bound.push({ event, handler });
    };
    // Caller-named events first, then ours. Callers that bind their own handlers to this
    // channel and index into a spy's calls (`bind.mock.calls[0]`) keep their indices as long
    // as the additions stay at the end.
    events?.forEach(bindReconcile);
    bindReconcile(PUSHER_SUBSCRIPTION_SUCCEEDED);
  }

  let timeout: ReturnType<typeof setTimeout> | undefined;
  let interval: ReturnType<typeof setInterval> | undefined;

  const stopPolling = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = undefined;
    }
    if (interval) {
      clearInterval(interval);
      interval = undefined;
    }
  };

  if (poll) {
    const armedAt = Date.now();
    const expiresAt = armedAt + poll.maxMs;
    const rawSince = poll.since;
    // Absent, NaN or non-positive → "not supplied". `Math.max(0, NaN)` is NaN and
    // `setTimeout(NaN)` fires on the next tick, so an unguarded bad value would silently
    // switch the grace period off for every job; epoch 0 would do the same by arithmetic.
    // Clamped to `delayMs` at the top so a `since` in the FUTURE (clock skew on a
    // server-generated timestamp) cannot stretch the first read beyond the normal grace
    // period — see `JobPollConfig.since` for what that does and does not promise about the
    // cap.
    const since =
      rawSince !== undefined && Number.isFinite(rawSince) && rawSince > 0
        ? rawSince
        : armedAt;
    const initialDelay = Math.min(
      poll.delayMs,
      Math.max(0, poll.delayMs - (armedAt - since))
    );
    const { intervalMs, onExpired } = poll;
    // At-most-once latch for `onExpired`. Past the cap the in-flight guard is dropped on
    // purpose (see `tick`), so ticks overlap and more than one can arrive here — with a 30s
    // interval and a read settling at or past a 30s router timeout, that is ordinary, not
    // exotic. The only caller that surfaces a verdict today survives a double call by
    // accident, because its own run-token test swallows the second; nothing about this
    // module's contract entitled it to that.
    let expiryFired = false;

    /**
     * One poll tick.
     *
     * @return {Promise<void>} Resolves once this tick's reconcile has settled.
     */
    const tick = async () => {
      // Measured BEFORE the reconcile, so a slow response cannot itself be the thing that
      // pushes the job past the cap...
      const expired = Date.now() > expiresAt;
      // Don't stack ticks on a read slower than the interval. Where the reconcile is a
      // react-query invalidation this is load-bearing: `refetch` defaults to
      // `cancelRefetch: true` and query functions take the abort signal, so an unguarded tick
      // ABORTS the in-flight read and starts another — forever, for any read slower than the
      // interval.
      //
      // PAST THE CAP the guard is dropped, deliberately. On a page whose only exit from a
      // wedged job is the client-side timeout, a reconcile that never settles would otherwise
      // pin the count above zero, no-op every later tick, and starve the one decision the
      // user depends on. Past the cap each tick therefore runs its own read and decides on
      // its own outcome — which is exactly what the unguarded poll this replaced did, and it
      // keeps trying until one of them answers. (If the caller's reconcile can never settle
      // at all, nothing here can help; that is the caller's promise to keep.)
      if (inFlightCount > 0 && !expired) return;
      await runReconcile();
      // ...and acted on AFTER it, so the last word belongs to the record. A job that had in
      // fact finished is resolved by the reconcile above; only then does the cap end a wait
      // that produced nothing. `disarmed` because that reconcile is awaited: the caller can
      // have torn this down while it was outstanding, and a teardown must not be followed by
      // a timeout for a job nobody is watching.
      if (!expired || disarmed || expiryFired) return;
      expiryFired = true;
      stopPolling();
      onExpired?.();
    };

    timeout = setTimeout(() => {
      timeout = undefined;
      // The interval is armed BEFORE the first tick, not after. A SYNCHRONOUS reconcile that
      // disarms from inside that first tick would otherwise reach `stopPolling()` while
      // `interval` is still undefined, clear nothing, and have the interval assigned on top
      // of it the moment the tick returned — polling forever, past unmount.
      interval = setInterval(() => void tick(), intervalMs);
      void tick();
    }, initialDelay);
  }

  return () => {
    disarmed = true;
    stopPolling();
    bound.forEach(({ event, handler }) => channel?.unbind(event, handler));
    bound.length = 0;
  };
}

/** {@link JobCompletionConfig} for the hook, which subscribes for you. */
export interface UseJobCompletionConfig extends Omit<
  JobCompletionConfig,
  'channel'
> {
  /**
   * Channel NAME to subscribe to. Null/undefined subscribes to nothing (the poll can still
   * arm), which is how a caller disarms the Pusher half without unmounting.
   */
  channel: string | null | undefined;
}

/**
 * Subscribes to `channel` and reconciles whenever the job might have moved: on
 * `pusher:subscription_succeeded` (which closes the publish-before-subscribe race — see
 * {@link PUSHER_SUBSCRIPTION_SUCCEEDED}), on each named event, and on the optional poll
 * backstop. Unsubscribes and disarms on change or unmount.
 *
 * USE THIS ONLY WHERE THE CALLING COMPONENT OWNS THE CHANNEL'S LIFETIME. `lib/pusher`'s
 * `subscribe` is a bare passthrough to `Pusher#subscribe` with NO reference counting, and
 * pusher-js returns the same Channel object for a name already in its registry — so if two
 * mounted components subscribe to the same channel NAME, the first to unmount unsubscribes
 * the channel out from under the other, which then goes silent with no error. Where a name is
 * shared, subscribe once at the shared owner and hand the channel object to
 * {@link armJobCompletion}, which never unsubscribes.
 *
 * `reconcile` and `poll.onExpired` are read through a ref at CALL time, so passing inline
 * closures over page state neither re-subscribes nor goes stale. `poll.since` is read once,
 * at ARM time — deliberately, so a value that moves with the data (a cache timestamp, say)
 * cannot restart the delay on every refetch and stop it ever firing. What DOES re-arm
 * everything is a change to `channel`, the event names, or whether/how the poll is
 * configured.
 *
 * @param {UseJobCompletionConfig} config - Channel name, triggers and the reconcile.
 */
export function useJobCompletion(config: UseJobCompletionConfig): void {
  const configRef = useRef(config);
  configRef.current = config;

  const { channel, events, poll } = config;
  // Array/object identities would re-subscribe on every render; their CONTENTS are the real
  // dependency. `\u0000` is the separator because no event name in this codebase contains one
  // and none plausibly would — not because Pusher forbids it, which is not something this
  // comment has checked. If one ever did, the round-trip would split it into two names that
  // never fire, which `binds every name when there is more than one event` would catch.
  const eventsKey = (events ?? []).join('\u0000');
  const pollArmed = Boolean(poll);
  const pollDelayMs = poll?.delayMs;
  const pollIntervalMs = poll?.intervalMs;
  const pollMaxMs = poll?.maxMs;

  useEffect(() => {
    if (!channel && !pollArmed) return undefined;
    const subscription = channel ? subscribe(channel) : null;
    // Same render as the `pollArmed`/`poll*Ms` above, so the fields are the ones the deps pin.
    const armedPoll = configRef.current.poll;
    const disarm = armJobCompletion({
      channel: subscription,
      events: eventsKey ? eventsKey.split('\u0000') : undefined,
      reconcile: () => configRef.current.reconcile(),
      poll: armedPoll
        ? {
            ...armedPoll,
            // Ref'd like `reconcile`: the core captures whatever it is handed at arm time, so
            // without this an inline closure would be frozen at the render that armed.
            onExpired: () => configRef.current.poll?.onExpired?.(),
          }
        : null,
    });
    return () => {
      disarm();
      subscription?.unsubscribe();
    };
  }, [channel, eventsKey, pollArmed, pollDelayMs, pollIntervalMs, pollMaxMs]);
}
